import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Mail, Lock, User, Phone, Store, Building2, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import SocialLoginButtons from '../components/common/SocialLoginButtons';

const Login = () => {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const { t } = useTranslation();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [isBusinessOwnerSignup, setIsBusinessOwnerSignup] = useState(false);
  const [step, setStep] = useState(1); // 1 = user signup, 2 = business signup
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    phone: '',
  });
  const [businessData, setBusinessData] = useState({
    business_name: '',
    display_name: '',
    cpf_cnpj: '',
    address: '',
    city: '',
    business_type: 'QUIOSQUE',
    service_radius: 50,
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleBusinessChange = (e) => {
    setBusinessData({ ...businessData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        const loggedUser = await login(formData.email, formData.password);
        toast.success('Login realizado com sucesso!');
        
        // Redirect based on account_type from backend
        const accountType = loggedUser.account_type || 'user';
        switch (accountType) {
          case 'admin':
            navigate('/admin/');
            break;
          case 'business_owner':
          case 'business_staff':
            navigate('/client-dashboard/');
            break;
          default:
            navigate('/');
        }
      } else if (isBusinessOwnerSignup && step === 1) {
        // Business owner signup - Step 1: Create user account
        const result = await register({
          email: formData.email,
          password: formData.password,
          full_name: formData.full_name,
          phone: formData.phone,
          is_business_owner: true
        });
        
        if (result?.requires_business_setup) {
          setStep(2);
          toast.success('Conta criada! Agora cadastre seu estabelecimento.');
        } else {
          // Fallback: auto-login
          toast.success('Cadastro realizado! Fazendo login...');
          const loggedUser = await login(formData.email, formData.password);
          const accountType = loggedUser.account_type || 'user';
          if (accountType === 'business_owner' || accountType === 'business_staff') {
            navigate('/client-dashboard/');
          } else {
            navigate('/');
          }
        }
      } else if (isBusinessOwnerSignup && step === 2) {
        // Business owner signup - Step 2: Complete business registration
        const completeResp = await fetch('/api/auth/complete-registration', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify(businessData)
        });
        const completeData = await completeResp.json();
        
        if (completeResp.ok) {
          toast.success('Estabelecimento cadastrado! Fazendo login...');
          const loggedUser = await login(formData.email, formData.password);
          const accountType = loggedUser.account_type || 'user';
          if (accountType === 'business_owner' || accountType === 'business_staff') {
            navigate('/client-dashboard/');
          } else {
            navigate('/');
          }
        } else {
          toast.error(completeData.detail || 'Erro ao completar cadastro.');
        }
      } else {
        // Regular user signup (customer)
        await register({
          email: formData.email,
          password: formData.password,
          full_name: formData.full_name,
          phone: formData.phone,
          is_business_owner: false
        });
        toast.success('Cadastro realizado! Fazendo login...');
        const loggedUser = await login(formData.email, formData.password);
        navigate('/');
      }
    } catch (error) {
      toast.error(
        error.response?.data?.detail || 'Erro ao fazer login. Verifique suas credenciais.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/10 via-background to-secondary/10 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img
            src="https://static.prod-images.emergentagent.com/jobs/38c5717c-99f2-4711-b6d9-c7c1cc9517e3/images/f02674562dcbb7e33a9e01d5c7f91e1404b16b69f9b73b7fbe740c041c7b44e6.png"
            alt="Beach Pay"
            className="w-24 h-24 mx-auto mb-4"
          />
          <h1 className="font-['Outfit'] text-3xl font-bold text-foreground">
            {t('login.title')}
          </h1>
          <p className="text-muted-foreground mt-2">
            {t('login.subtitle')}
          </p>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <div className="flex gap-2 mb-6">
            <button
              data-testid="login-tab"
              onClick={() => setIsLogin(true)}
              className={`flex-1 py-2 rounded-lg font-semibold transition-colors ${
                isLogin
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground'
              }`}
            >
              {t('login.login')}
            </button>
            <button
              data-testid="register-tab"
              onClick={() => setIsLogin(false)}
              className={`flex-1 py-2 rounded-lg font-semibold transition-colors ${
                !isLogin
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground'
              }`}
            >
              {t('login.register')}
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && step === 1 && (
              <>
                {/* Account type toggle */}
                <div className="flex gap-2 mb-2">
                  <button
                    type="button"
                    onClick={() => setIsBusinessOwnerSignup(false)}
                    className={`flex-1 py-2.5 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-all ${
                      !isBusinessOwnerSignup
                        ? 'bg-primary text-primary-foreground shadow-md'
                        : 'bg-muted text-muted-foreground hover:bg-muted/80'
                    }`}
                  >
                    <User className="w-4 h-4" />
                    Pessoa Física
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsBusinessOwnerSignup(true)}
                    className={`flex-1 py-2.5 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-all ${
                      isBusinessOwnerSignup
                        ? 'bg-primary text-primary-foreground shadow-md'
                        : 'bg-muted text-muted-foreground hover:bg-muted/80'
                    }`}
                  >
                    <Store className="w-4 h-4" />
                    Meu Negócio
                  </button>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    {t('login.fullName')}
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <input
                      type="text"
                      name="full_name"
                      value={formData.full_name}
                      onChange={handleChange}
                      className="w-full pl-10 pr-4 py-3 border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                      required={!isLogin}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    {t('login.phone')}
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+55 48 99999-9999"
                      className="w-full pl-10 pr-4 py-3 border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                      required={!isLogin}
                    />
                  </div>
                </div>
              </>
            )}

            {/* Step 2: Business registration */}
            {isBusinessOwnerSignup && step === 2 && (
              <>
                <div className="text-center mb-2">
                  <Building2 className="w-8 h-8 mx-auto text-primary mb-1" />
                  <p className="text-sm font-medium text-foreground">Cadastre seu estabelecimento</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Nome do Estabelecimento *
                  </label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <input
                      type="text"
                      name="display_name"
                      value={businessData.display_name}
                      onChange={handleBusinessChange}
                      placeholder="Ex: Bar da Praia"
                      className="w-full pl-10 pr-4 py-3 border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Nome Empresarial (Razão Social)
                  </label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <input
                      type="text"
                      name="business_name"
                      value={businessData.business_name}
                      onChange={handleBusinessChange}
                      placeholder="Ex: Bar da Praia LTDA"
                      className="w-full pl-10 pr-4 py-3 border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    CPF/CNPJ
                  </label>
                  <input
                    type="text"
                    name="cpf_cnpj"
                    value={businessData.cpf_cnpj}
                    onChange={handleBusinessChange}
                    placeholder="00.000.000/0001-00"
                    className="w-full px-4 py-3 border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Endereço
                  </label>
                  <input
                    type="text"
                    name="address"
                    value={businessData.address}
                    onChange={handleBusinessChange}
                    placeholder="Rua da Praia, 123"
                    className="w-full px-4 py-3 border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Cidade
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={businessData.city}
                    onChange={handleBusinessChange}
                    placeholder="Florianópolis"
                    className="w-full px-4 py-3 border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    className="px-4 py-2 bg-muted text-foreground rounded-xl text-sm font-semibold hover:bg-muted/80"
                  >
                    Voltar
                  </button>
                </div>
              </>
            )}

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                {t('login.email')}
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <input
                  data-testid="email-input"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full pl-10 pr-4 py-3 border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                {t('login.password')}
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <input
                  data-testid="password-input"
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className="w-full pl-10 pr-4 py-3 border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                />
              </div>
            </div>

            <button
              data-testid="submit-button"
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-primary text-primary-foreground rounded-full font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 active:scale-95"
            >
              {loading
                ? t('login.wait')
                : isLogin
                  ? t('login.submit')
                  : isBusinessOwnerSignup && step === 1
                    ? 'Próximo (Conta)'
                    : isBusinessOwnerSignup && step === 2
                      ? 'Cadastrar Estabelecimento'
                      : t('login.submitRegister')}
            </button>
          </form>

          {isLogin && (
            <div className="mt-4 text-center">
              <p className="text-sm text-muted-foreground">
                {t('login.testUsers')}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Cliente: cliente@teste.com / cliente123
              </p>
            </div>
          )}

          <SocialLoginButtons />
        </div>

        <div className="mt-6 text-center">
          <Link to="/" className="text-sm text-primary hover:underline">
            {t('login.backToHome')}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Login;
